import React from 'react';
import { Section } from '../components/ui/Section';
import { Button } from '../components/ui/Button';
import { Heart, GraduationCap, Stethoscope, MapPin } from 'lucide-react';
export function AboutPage() {
  return <>
      <Section className="py-20" background="offwhite" data-id="element-251">
        <div className="text-center mb-12" data-id="element-252">
          <h1 className="text-4xl md:text-5xl font-playfair font-bold text-primary-deep-blue mb-6" data-id="element-253">
            About Farasha Medical Aid
          </h1>
          <p className="text-xl font-inter text-neutral-charcoal max-w-3xl mx-auto" data-id="element-254">
            Two students saw a need others missed.
          </p>
        </div>
      </Section>

      <Section background="white" data-id="element-255">
        <div className="max-w-4xl mx-auto mb-16" data-id="element-256">
          <h2 className="text-3xl font-playfair font-bold text-primary-deep-blue mb-6" data-id="element-257">
            Our Story
          </h2>
          <div className="space-y-4 text-lg font-inter text-neutral-charcoal" data-id="element-258">
            <p data-id="element-259">
              In Egypt's Eastern Desert, ancient monasteries have preserved
              Christian faith and culture for nearly two millennia. These aren't
              museums—they're living communities where monks maintain centuries
              of tradition while serving surrounding villages. And they're
              struggling.
            </p>
            <p data-id="element-260">
              Farasha Medical Aid began when we visited these communities and
              discovered a striking gap: dedicated healthcare practitioners
              working with inadequate supplies, serving populations invisible to
              Egypt's urban-focused healthcare system. We returned to America
              knowing we could help.
            </p>
            <p data-id="element-261">
              The name "Farasha" (فراشة) means "butterfly" in
              Arabic—representing transformation and hope. We're building
              something small that creates outsized impact: a reliable supply
              chain connecting American medical resources with Egyptian
              communities that need them most.
            </p>
          </div>
        </div>
      </Section>

      <Section background="sand" data-id="element-262">
        <div className="mb-12" data-id="element-263">
          <h2 className="text-3xl font-playfair font-bold text-primary-deep-blue mb-12 text-center" data-id="element-264">
            Meet Our Founders
          </h2>

          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto" data-id="element-265">
            <div className="bg-white p-8 rounded-xl shadow-md" data-id="element-266">
              <div className="mb-6" data-id="element-267">
                <div className="rounded-xl overflow-hidden shadow-lg mb-4" data-id="element-268">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/nbJVafVZgRcxD2AxVMVQyD/0225681B-F332-4CF8-BE10-DDAFB26CF5D9_1_105_c.jpg" alt="Natasha Khalil, Co-Founder of Farasha Medical Aid" className="w-full h-64 object-cover" data-id="element-269" />
                </div>
                <h3 className="text-2xl font-playfair font-bold text-primary-deep-blue mb-2" data-id="element-270">
                  Natasha Khalil
                </h3>
                <p className="font-inter text-primary-royal-blue mb-4" data-id="element-271">
                  Co-Founder & Executive Director
                </p>
              </div>
              <div className="space-y-3 text-base font-inter text-neutral-charcoal" data-id="element-272">
                <p data-id="element-273">
                  Natasha Khalil is a first-generation Egyptian-Mexican American
                  from Los Angeles, whose immigrant parents instilled in her the
                  values of resilience, humility, and service.
                </p>
                <p data-id="element-274">
                  Inspired by her father's experiences in Egypt, where
                  healthcare access was often limited, Natasha committed herself
                  to advancing health with dignity and equity for all.
                </p>
                <p data-id="element-275">
                  At Loyola Marymount University, she earned a B.A. in
                  Psychology, studying the intersection of mental health,
                  physical wellness, and social conditions that shape health
                  outcomes. Currently preparing for medical school, she leads
                  Farasha's logistics and monastery partnerships.
                </p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-md" data-id="element-276">
              <div className="mb-6" data-id="element-277">
                <div className="rounded-xl overflow-hidden shadow-lg mb-4" data-id="element-278">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/kaALpsMzDtAjX55veVz28S/3EF4819E-249B-4E37-B8FE-31CEA4263BEE_1_105_c.jpg" alt="Nicole Khalil, Co-Founder of Farasha Medical Aid" className="w-full h-64 object-cover" data-id="element-279" />
                </div>
                <h3 className="text-2xl font-playfair font-bold text-primary-deep-blue mb-2" data-id="element-280">
                  Nicole Khalil
                </h3>
                <p className="font-inter text-primary-royal-blue mb-4" data-id="element-281">
                  Co-Founder & Communications Director
                </p>
              </div>
              <div className="space-y-3 text-base font-inter text-neutral-charcoal" data-id="element-282">
                <p data-id="element-283">
                  Nicole Khalil is a student at Loyola Marymount University
                  pursuing a Bachelor of Arts in English and minor in
                  Theological Studies.
                </p>
                <p data-id="element-284">
                  While pursuing her bachelor's degree, Nicole noticed that
                  oftentimes the most important stories are suppressed,
                  overshadowed, or entirely forgotten by dominant cultural
                  narratives.
                </p>
                <p data-id="element-285">
                  Through her blog posts, she strives to counteract this trend
                  by revealing the trials and tribulations of the Coptic people.
                  She hopes, in doing so, her posts will incite cultural
                  awareness and meaningful action toward the pursuit of change.
                </p>
                <p data-id="element-286">
                  Nicole leads Farasha's storytelling, educational content, and
                  donor communications.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section background="white" data-id="element-287">
        <div className="max-w-4xl mx-auto" data-id="element-288">
          <h2 className="text-3xl font-playfair font-bold text-primary-deep-blue mb-6 text-center" data-id="element-289">
            The Meaning Behind Farasha
          </h2>
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-290">
            <div className="space-y-4 text-lg font-inter text-neutral-charcoal" data-id="element-291">
              <p data-id="element-292">
                "Farasha" means "butterfly" in Arabic, honoring Natasha's late
                grandmother, Kamilia. The name reflects our belief that focused,
                evidence-based interventions can create meaningful
                transformation—just as a butterfly effect can generate
                significant change from small actions.
              </p>
              <p data-id="element-293">
                Our logo combines the butterfly symbol with the medical
                caduceus, representing our commitment to healthcare
                transformation and our Egyptian heritage.
              </p>
            </div>
            <div className="flex justify-center items-center" data-id="element-294">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/vJ4LnEANA2qYvkkH1pNfQy/design-d5307099-5cbb-4264-b386-3c96f879a243.png" alt="Farasha Medical Aid Logo" className="h-64 w-auto" data-id="element-295" />
            </div>
          </div>
        </div>
      </Section>

      <Section background="deep-blue" data-id="element-296">
        <div className="text-center" data-id="element-297">
          <h2 className="text-3xl font-playfair font-bold mb-6" data-id="element-298">
            Join Our Founding Mission
          </h2>
          <p className="text-xl font-inter mb-8 max-w-3xl mx-auto" data-id="element-299">
            Help us deliver our inaugural shipment of medical supplies to
            monastery communities in Egypt.
          </p>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-300">
            <Button href="https://www.paypal.com/biz/profile/FarashaMedicalAid" size="lg" data-id="element-301">
              Donate Now
            </Button>
            <Button href="/get-involved" variant="secondary" size="lg" data-id="element-302">
              Get Involved
            </Button>
          </div>
        </div>
      </Section>
    </>;
}